import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import infoIcon from "../../../assets/images/info-icon.svg";

const PANEL_WIDTH = 360;
const DIALOG_PADDING = 16;
const MOBILE_MAX_WIDTH = 767;

const InfoPanel = ({ label, content }) => {
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [dialogEl, setDialogEl] = useState(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: PANEL_WIDTH });
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`);
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const updatePosition = () => {
    const button = buttonRef.current;
    const dialog = button?.closest(".signup-modal__dialog");
    if (!button || !dialog) return;

    setDialogEl(dialog);

    const dialogRect = dialog.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    const width = Math.min(PANEL_WIDTH, dialogRect.width - DIALOG_PADDING * 2);

    let left = buttonRect.left - dialogRect.left;
    if (left + width > dialogRect.width - DIALOG_PADDING) {
      left = dialogRect.width - width - DIALOG_PADDING;
    }
    left = Math.max(DIALOG_PADDING, left);

    let top = buttonRect.bottom - dialogRect.top + 8;
    const panelHeight = panelRef.current?.offsetHeight || 0;

    if (panelHeight && top + panelHeight > dialogRect.height - DIALOG_PADDING) {
      top = Math.max(DIALOG_PADDING, buttonRect.top - dialogRect.top - panelHeight - 8);
    }

    setPosition({ top, left, width });
  };

  useLayoutEffect(() => {
    if (!open || isMobile) return;
    updatePosition();
  }, [open, content, isMobile]);

  useEffect(() => {
    if (!open || isMobile) return undefined;

    const onPointerDown = (event) => {
      if (
        !wrapRef.current?.contains(event.target) &&
        !panelRef.current?.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    const onReposition = () => updatePosition();

    document.addEventListener("mousedown", onPointerDown);
    window.addEventListener("scroll", onReposition, true);
    window.addEventListener("resize", onReposition);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("scroll", onReposition, true);
      window.removeEventListener("resize", onReposition);
    };
  }, [open, isMobile]);

  const handleToggle = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next && buttonRef.current) {
        setDialogEl(buttonRef.current.closest(".signup-modal__dialog"));
      }
      return next;
    });
  };

  if (!content) return null;

  const panel = open ? (
    <div
      ref={panelRef}
      className={`signup-form__info-panel${
        isMobile ? " signup-form__info-panel--inline" : ""
      }`}
      role="tooltip"
      style={
        isMobile
          ? undefined
          : {
              top: `${position.top}px`,
              left: `${position.left}px`,
              width: `${position.width}px`,
            }
      }
    >
      <p>{content.intro}</p>
      <ul>
        {content.items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  ) : null;

  return (
    <div
      className={`signup-form__info-wrap${open && isMobile ? " is-open" : ""}`}
      ref={wrapRef}
    >
      <button
        ref={buttonRef}
        type="button"
        className="signup-form__info"
        aria-label={label}
        aria-expanded={open}
        onClick={handleToggle}
      >
        <img src={infoIcon} alt="" width={18} height={18} />
      </button>
      {isMobile
        ? panel
        : panel && dialogEl
          ? createPortal(panel, dialogEl)
          : null}
    </div>
  );
};

export default InfoPanel;
